import { FLOW, TEAM_STEP, planRef } from "./flowCopy";
import type { PlanFlow } from "./planFlow";
import type { PlanTab } from "./shared";
import type { AccountPlanOverview } from "../../Types/AccountPlan";
import { STALLED_LINE } from "../../Utils/AccountPlans/queueText";

/**
 * The page's one next step per state (UI tour A, items 4 and 17, 2026-11-03). The
 * header's primary button, the empty Confirm plan / Publish tabs and their next-step
 * cards all read this, so the button doesn't change from tab to tab in the same
 * state, and it is named after the step it leads to, never "Go to".
 */
export interface PlanNextStep {
  /** The step's name ("Confirm plan", "Review briefing", "Publish"…). */
  label: string;
  /** The tab it opens. */
  tab: PlanTab;
  /** Extra URL params (the version to confirm, focus=upload / focus=problem). */
  extra?: Record<string, string | null>;
  /**
   * Shown on its own tab too: "Upload new version" on an overdue plan scrolls to the
   * drop zone there (item 18). Every other step hides on the tab it points at.
   */
  everyTab?: boolean;
}

export interface NextStepWho {
  /** The viewer acts as the AE (uploads, confirms, publishes). */
  ae: boolean;
  /** The viewer acts as the CSM (verifies their sections). */
  csm: boolean;
}

const upload = (extra?: Record<string, string | null>): PlanNextStep => ({
  label: FLOW.upload,
  tab: "plan",
  extra,
});

export const planNextStep = (
  data: AccountPlanOverview,
  flow: PlanFlow,
  who: NextStepWho
): PlanNextStep | null => {
  const review = data.current_review;
  const inReview =
    !!flow.current &&
    flow.currentConfirmed &&
    !flow.currentPublished &&
    !!review &&
    review.status !== "none" &&
    !review.published;

  if (!who.ae) {
    // The CSM's only step is verifying their own sections of a draft in review.
    if (who.csm && inReview && (review?.progress.CSM?.pending?.length ?? 0) > 0)
      return { label: TEAM_STEP.sponsor, tab: "sponsor" };
    return null;
  }

  if (flow.toConfirm)
    return {
      label: FLOW.reviewUpload,
      tab: "viewer",
      extra:
        flow.toConfirm.id === data.current_file_id
          ? undefined
          : { file: flow.toConfirm.id },
    };
  // The system is reading: nothing to press.
  if (flow.reading) return null;
  // The newest upload stopped and can't continue: the problem is on the Upload step.
  if (flow.stopped) return upload({ focus: "problem" });
  if (!flow.current) {
    // Only the first upload being read (no `reading`: there is no current to be newer than).
    if (data.files.some(f => f.status === "processing" && !f.discarded))
      return null;
    return upload();
  }
  // Written, or stalled for an Izba admin: nothing to review or publish yet (item 1).
  if (flow.briefing) return null;
  if (flow.currentPublished)
    return flow.overdue
      ? {
          label: FLOW.uploadNewVersion,
          tab: "plan",
          extra: { focus: "upload" },
          everyTab: true,
        }
      : null;
  if (inReview)
    return flow.readyToPublish
      ? { label: TEAM_STEP.publish, tab: "publish" }
      : { label: TEAM_STEP.sponsor, tab: "sponsor" };
  // Confirmed before the Confirm step started the briefing: "Write it" is on Review briefing.
  return { label: TEAM_STEP.sponsor, tab: "sponsor" };
};

/** Whether the header shows the step on this tab (item 17: never the tab you're on). */
export const showNextStepOn = (
  step: PlanNextStep | null,
  activeStep: PlanTab | null
): step is PlanNextStep =>
  !!step && (!!step.everyTab || step.tab !== activeStep);

/**
 * Why a step has nothing to show yet, in the real state's words (item 4): never "No
 * plan uploaded yet" while a file is being read, stalled, stopped or waiting on the
 * AE's OK. `text` is the empty panel's line; the action, if any, is planNextStep's.
 */
export type PlanGapKind =
  | "none"
  | "reading"
  | "read_stalled"
  | "stopped"
  | "confirm"
  | "writing"
  | "brief_stalled"
  | "not_written"
  | "review";

export interface PlanGap {
  kind: PlanGapKind;
  text: string;
}

export const planGapOf = (
  data: AccountPlanOverview,
  flow: PlanFlow,
  isAE: boolean
): PlanGap => {
  if (flow.toConfirm)
    return {
      kind: "confirm",
      text: isAE
        ? "Confirm the plan first. The draft briefing is written once it's confirmed."
        : `${FLOW.waitingConfirmPlan}. The draft briefing is written once it's confirmed.`,
    };
  const readingNow =
    flow.reading ||
    (!flow.current
      ? data.files.find(f => f.status === "processing" && !f.discarded) || null
      : null);
  if (readingNow)
    return readingNow.progress?.stalled
      ? { kind: "read_stalled", text: STALLED_LINE }
      : {
          kind: "reading",
          text: `${FLOW.parsing}. ${
            flow.current ? "The new upload" : "The plan"
          } shows here once it's read.`,
        };
  if (flow.stopped)
    return {
      kind: "stopped",
      text: isAE
        ? "The new upload needs review. See why on the Upload step."
        : FLOW.aeReviewingUpload,
    };
  if (!flow.current)
    return {
      kind: "none",
      text: isAE ? "No plan uploaded yet." : FLOW.waitingAeUpload,
    };
  if (flow.briefing === "stalled")
    return { kind: "brief_stalled", text: STALLED_LINE };
  if (flow.briefing === "writing")
    return {
      kind: "writing",
      text: `Writing the draft briefing for ${planRef(flow.current)}. It's ready to review once it's written.`,
    };
  // Written, but its review hasn't reached this page yet.
  if (flow.current.sponsor_view_status === "ready")
    return { kind: "review", text: "Review the draft briefing first." };
  return {
    kind: "not_written",
    text: `The draft briefing for ${planRef(flow.current)} hasn't been written yet.`,
  };
};
